'use client'

import { useState } from 'react'
import { format, subDays, startOfMonth, endOfMonth, subMonths } from 'date-fns'
import {
  Calendar,
  Download,
  FileSpreadsheet,
  Loader2,
  Users,
  Clock,
  Check,
  X,
  ChevronDown,
  ChevronUp,
  Columns3,
} from 'lucide-react'
import { DayPicker } from 'react-day-picker'

import {
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { popModal } from '@/components/modals'
import { toast } from 'sonner'
import { cn } from '@/lib/utils'
import { convertToCSV, downloadAsCSV } from '@/lib/utils'
import type { User } from '@/lib/types'

interface DownloadUsersModalProps {
  currentSearch?: string
  currentSort?: string
  currentSortDirection?: 'asc' | 'desc'
}

type DatePreset = {
  label: string
  getValue: () => { from: Date; to: Date }
}

// All available export fields
const EXPORT_FIELDS = [
  { key: 'ID', label: 'ID', category: 'basic' },
  { key: 'Email', label: 'Email', category: 'basic' },
  { key: 'Username', label: 'Username', category: 'basic' },
  { key: 'Total Projects', label: 'Total Projects', category: 'basic' },
  { key: 'Subscription Tier', label: 'Subscription Tier', category: 'basic' },
  { key: 'Coin', label: 'Coin', category: 'basic' },
  { key: 'User Role', label: 'User Role', category: 'status' },
  { key: 'Locale', label: 'Locale', category: 'status' },
  { key: 'Language', label: 'Language', category: 'status' },
  { key: 'Referred By', label: 'Referred By', category: 'acquisition' },
  { key: 'Referral Code', label: 'Referral Code', category: 'acquisition' },
  { key: 'Offer Code', label: 'Offer Code', category: 'acquisition' },
  {
    key: 'Acquisition Source',
    label: 'Acquisition Source',
    category: 'acquisition',
  },
  { key: 'Marketing Email', label: 'Marketing Email', category: 'status' },
  {
    key: 'Onboarding Questions',
    label: 'Onboarding Questions',
    category: 'engagement',
  },
  {
    key: 'Unsubscription Feedback',
    label: 'Unsubscription Feedback',
    category: 'engagement',
  },
  { key: 'Created At', label: 'Created At', category: 'time' },
  { key: 'Update Time', label: 'Update Time', category: 'time' },
  { key: 'Last Login Time', label: 'Last Login Time', category: 'time' },
  { key: 'Last Coding Time', label: 'Last Coding Time', category: 'time' },
] as const

type FieldKey = (typeof EXPORT_FIELDS)[number]['key']

// Default selected fields
const DEFAULT_SELECTED_FIELDS: FieldKey[] = [
  'ID',
  'Email',
  'Username',
  'Total Projects',
  'Subscription Tier',
  'User Role',
  'Acquisition Source',
  'Coin',
  'Created At',
  'Last Login Time',
]

const DATE_PRESETS: DatePreset[] = [
  {
    label: 'Today',
    getValue: () => {
      const today = new Date()
      return { from: today, to: today }
    },
  },
  {
    label: 'Yesterday',
    getValue: () => {
      const yesterday = subDays(new Date(), 1)
      return { from: yesterday, to: yesterday }
    },
  },
  {
    label: 'Last 7 days',
    getValue: () => ({
      from: subDays(new Date(), 6),
      to: new Date(),
    }),
  },
  {
    label: 'Last 30 days',
    getValue: () => ({
      from: subDays(new Date(), 29),
      to: new Date(),
    }),
  },
  {
    label: 'This month',
    getValue: () => ({
      from: startOfMonth(new Date()),
      to: new Date(),
    }),
  },
  {
    label: 'Last month',
    getValue: () => {
      const lastMonth = subMonths(new Date(), 1)
      return {
        from: startOfMonth(lastMonth),
        to: endOfMonth(lastMonth),
      }
    },
  },
]

export default function DownloadUsersModal({
  currentSearch,
  currentSort,
  currentSortDirection,
}: DownloadUsersModalProps) {
  const [isDownloading, setIsDownloading] = useState(false)
  const [downloadMode, setDownloadMode] = useState<'all' | 'dateRange'>('all')
  const [dateRange, setDateRange] = useState<{
    from: Date | undefined
    to: Date | undefined
  }>({ from: undefined, to: undefined })
  const [selectedPreset, setSelectedPreset] = useState<string | null>(null)
  const [selectedFields, setSelectedFields] = useState<Set<FieldKey>>(
    new Set(DEFAULT_SELECTED_FIELDS),
  )
  const [showFieldSelector, setShowFieldSelector] = useState(false)

  const toggleField = (field: FieldKey) => {
    const newSelected = new Set(selectedFields)
    if (newSelected.has(field)) {
      newSelected.delete(field)
    } else {
      newSelected.add(field)
    }
    setSelectedFields(newSelected)
  }

  const selectAllFields = () => {
    setSelectedFields(new Set(EXPORT_FIELDS.map((f) => f.key)))
  }

  const clearAllFields = () => {
    setSelectedFields(new Set())
  }

  const selectDefaultFields = () => {
    setSelectedFields(new Set(DEFAULT_SELECTED_FIELDS))
  }

  const handleDownload = async () => {
    setIsDownloading(true)
    try {
      const params = new URLSearchParams()
      params.set('limit', '50000') // Large limit for export

      if (currentSearch) params.set('search', currentSearch)
      if (currentSort) {
        params.set('sort', currentSort)
        params.set('order', currentSortDirection || 'desc')
      }

      if (downloadMode === 'dateRange' && dateRange.from) {
        params.set('created_from', dateRange.from.toISOString())
        if (dateRange.to) {
          params.set('created_to', dateRange.to.toISOString())
        }
      }

      const response = await fetch(`/api/dashboard/users?${params}`)
      if (!response.ok) throw new Error('Failed to fetch users data')

      const users: User[] = await response.json()

      if (users.length === 0) {
        toast.info('No data to export')
        return
      }

      // Helper function to get field value
      const getFieldValue = (user: User, field: FieldKey): string | number => {
        const subscriptionTier =
          (user.subscription as any)?.plan ||
          (user.account_type === 'premium' ? 'premium' : 'free')

        const userRoles = user.roles || []
        const rolesDisplay = userRoles.length > 0 ? userRoles.join(', ') : ''

        const acqSourceAnswer = user.onboarding_answers?.find(
          (answer) =>
            answer.question_id === 'acq_source' ||
            answer.survey_key === 'acq_source',
        )
        const acquisitionSource = acqSourceAnswer
          ? [
              ...acqSourceAnswer.selected_options,
              ...(acqSourceAnswer.custom_input
                ? [acqSourceAnswer.custom_input]
                : []),
            ].join(', ')
          : ''

        const projectSubscriptions = (user as any).project_subscriptions || []
        const workspaceSubscriptions =
          (user as any).workspace_subscriptions || []
        const allSubscriptions = [
          ...projectSubscriptions,
          ...workspaceSubscriptions,
        ] as any[]
        const unsubscriptionFeedbacks = allSubscriptions
          .filter(
            (sub) =>
              sub.cancellation_feedback?.reason &&
              sub.cancellation_feedback.reason.trim().length > 0,
          )
          .map((sub) => sub.cancellation_feedback.reason)
          .join('; ')

        const onboardingAnswers =
          user.onboarding_answers
            ?.map(
              (answer) =>
                `${answer.survey_key || answer.question_id}: ${answer.selected_options.join(', ')}${answer.custom_input ? ` (${answer.custom_input})` : ''}`,
            )
            .join('; ') || ''

        const marketingEmail = user.email_preferences?.marketing_emails
        const marketingEmailDisplay =
          marketingEmail === true
            ? 'Subscribed'
            : marketingEmail === false
              ? 'Unsubscribed'
              : ''

        switch (field) {
          case 'ID':
            return user._id.toString()
          case 'Email':
            return user.email || ''
          case 'Username':
            return user.username || ''
          case 'Total Projects':
            return (user as any).total_projects || 0
          case 'Subscription Tier':
            return subscriptionTier
          case 'Coin':
            return typeof user.coin === 'number' ? user.coin : 0
          case 'User Role':
            return rolesDisplay
          case 'Locale':
            return user.locale || ''
          case 'Language':
            return user.language || ''
          case 'Referred By':
            return user.referred_by || ''
          case 'Referral Code':
            return user.referral_code || ''
          case 'Offer Code':
            return user.offer_code || ''
          case 'Acquisition Source':
            return acquisitionSource
          case 'Marketing Email':
            return marketingEmailDisplay
          case 'Onboarding Questions':
            return onboardingAnswers
          case 'Unsubscription Feedback':
            return unsubscriptionFeedbacks
          case 'Created At':
            return user.create_time
              ? format(new Date(user.create_time), 'yyyy-MM-dd HH:mm:ss')
              : ''
          case 'Update Time':
            return user.update_time
              ? format(new Date(user.update_time), 'yyyy-MM-dd HH:mm:ss')
              : ''
          case 'Last Login Time':
            return user.last_login_time
              ? format(new Date(user.last_login_time), 'yyyy-MM-dd HH:mm:ss')
              : ''
          case 'Last Coding Time':
            return user.last_coding_time
              ? format(new Date(user.last_coding_time), 'yyyy-MM-dd HH:mm:ss')
              : ''
          default:
            return ''
        }
      }

      // Get ordered selected fields
      const orderedFields = EXPORT_FIELDS.filter((f) =>
        selectedFields.has(f.key),
      )

      // Prepare CSV data with only selected fields
      const csvData = users.map((user) => {
        const row: Record<string, string | number> = {}
        for (const field of orderedFields) {
          row[field.key] = getFieldValue(user, field.key)
        }
        return row
      })

      const columns = orderedFields.map((f) => ({
        name: f.key,
        type:
          f.key === 'Coin' || f.key === 'Total Projects' ? 'number' : 'string',
      }))

      const csvContent = convertToCSV(csvData, columns)
      const dateSuffix =
        downloadMode === 'dateRange' && dateRange.from
          ? `-${format(dateRange.from, 'yyyy-MM-dd')}${dateRange.to ? `-to-${format(dateRange.to, 'yyyy-MM-dd')}` : ''}`
          : '-all'
      const fileName = `users${dateSuffix}-${format(new Date(), 'yyyy-MM-dd-HHmm')}.csv`
      downloadAsCSV(csvContent, fileName)

      const dateRangeText =
        downloadMode === 'dateRange' && dateRange.from
          ? ` (${format(dateRange.from, 'MMM dd')}${dateRange.to ? ` - ${format(dateRange.to, 'MMM dd')}` : ''})`
          : ''
      toast.success(`Exported ${users.length} users${dateRangeText} to CSV`)
      popModal()
    } catch (error) {
      console.error('Export failed:', error)
      toast.error('Failed to export users data')
    } finally {
      setIsDownloading(false)
    }
  }

  const selectPreset = (preset: DatePreset) => {
    const { from, to } = preset.getValue()
    setDateRange({ from, to })
    setSelectedPreset(preset.label)
    setDownloadMode('dateRange')
  }

  const clearDateRange = () => {
    setDateRange({ from: undefined, to: undefined })
    setSelectedPreset(null)
  }

  return (
    <DialogContent className="sm:max-w-[680px] p-0 bg-[#141414] border-[#2a2a2a] overflow-hidden">
      {/* Header */}
      <DialogHeader className="px-6 pt-6 pb-4 border-b border-[#2a2a2a]">
        <DialogTitle className="flex items-center gap-3 text-white">
          <div className="p-2 rounded-lg bg-amber-500/10 border border-amber-500/20">
            <FileSpreadsheet className="h-5 w-5 text-amber-500" />
          </div>
          <div>
            <h2 className="text-lg font-semibold">Export Users</h2>
            <p className="text-sm font-normal text-gray-500 mt-0.5">
              Download user data as CSV
            </p>
          </div>
        </DialogTitle>
      </DialogHeader>

      <div className="p-6 space-y-5 max-h-[70vh] overflow-y-auto">
        {/* Field Selection */}
        <div className="space-y-3">
          <button
            onClick={() => setShowFieldSelector(!showFieldSelector)}
            className="w-full flex items-center justify-between p-4 rounded-xl bg-[#1a1a1a] border border-[#2a2a2a] hover:border-[#3a3a3a] transition-all"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#252525] flex items-center justify-center">
                <Columns3 className="w-5 h-5 text-amber-500" />
              </div>
              <div className="text-left">
                <h3 className="font-semibold text-white">Select Fields</h3>
                <p className="text-xs text-gray-500">
                  {selectedFields.size} of {EXPORT_FIELDS.length} fields
                  selected
                </p>
              </div>
            </div>
            {showFieldSelector ? (
              <ChevronUp className="w-5 h-5 text-gray-400" />
            ) : (
              <ChevronDown className="w-5 h-5 text-gray-400" />
            )}
          </button>

          {showFieldSelector && (
            <div className="bg-[#1a1a1a] rounded-xl border border-[#2a2a2a] p-4 animate-in fade-in slide-in-from-top-2 duration-200">
              {/* Quick Actions */}
              <div className="flex items-center gap-2 mb-4 pb-3 border-b border-[#2a2a2a]">
                <button
                  onClick={selectAllFields}
                  className="px-3 py-1.5 text-xs font-medium bg-[#252525] text-gray-300 hover:text-white hover:bg-[#333] rounded-lg transition-colors"
                >
                  Select All
                </button>
                <button
                  onClick={selectDefaultFields}
                  className="px-3 py-1.5 text-xs font-medium bg-[#252525] text-gray-300 hover:text-white hover:bg-[#333] rounded-lg transition-colors"
                >
                  Default
                </button>
                <button
                  onClick={clearAllFields}
                  className="px-3 py-1.5 text-xs font-medium bg-[#252525] text-gray-300 hover:text-white hover:bg-[#333] rounded-lg transition-colors"
                >
                  Clear All
                </button>
              </div>

              {/* Field Categories */}
              <div className="space-y-4">
                {/* Basic Info */}
                <div>
                  <div className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
                    Basic Info
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {EXPORT_FIELDS.filter((f) => f.category === 'basic').map(
                      (field) => (
                        <button
                          key={field.key}
                          onClick={() => toggleField(field.key)}
                          className={cn(
                            'px-3 py-1.5 rounded-lg text-xs font-medium transition-all flex items-center gap-1.5',
                            selectedFields.has(field.key)
                              ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30'
                              : 'bg-[#252525] text-gray-400 hover:text-white border border-transparent hover:border-[#444]',
                          )}
                        >
                          {selectedFields.has(field.key) && (
                            <Check className="w-3 h-3" />
                          )}
                          {field.label}
                        </button>
                      ),
                    )}
                  </div>
                </div>

                {/* User Status */}
                <div>
                  <div className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
                    User Status
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {EXPORT_FIELDS.filter((f) => f.category === 'status').map(
                      (field) => (
                        <button
                          key={field.key}
                          onClick={() => toggleField(field.key)}
                          className={cn(
                            'px-3 py-1.5 rounded-lg text-xs font-medium transition-all flex items-center gap-1.5',
                            selectedFields.has(field.key)
                              ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30'
                              : 'bg-[#252525] text-gray-400 hover:text-white border border-transparent hover:border-[#444]',
                          )}
                        >
                          {selectedFields.has(field.key) && (
                            <Check className="w-3 h-3" />
                          )}
                          {field.label}
                        </button>
                      ),
                    )}
                  </div>
                </div>

                {/* Acquisition */}
                <div>
                  <div className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
                    Acquisition
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {EXPORT_FIELDS.filter(
                      (f) => f.category === 'acquisition',
                    ).map((field) => (
                      <button
                        key={field.key}
                        onClick={() => toggleField(field.key)}
                        className={cn(
                          'px-3 py-1.5 rounded-lg text-xs font-medium transition-all flex items-center gap-1.5',
                          selectedFields.has(field.key)
                            ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30'
                            : 'bg-[#252525] text-gray-400 hover:text-white border border-transparent hover:border-[#444]',
                        )}
                      >
                        {selectedFields.has(field.key) && (
                          <Check className="w-3 h-3" />
                        )}
                        {field.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Engagement */}
                <div>
                  <div className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
                    Engagement
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {EXPORT_FIELDS.filter(
                      (f) => f.category === 'engagement',
                    ).map((field) => (
                      <button
                        key={field.key}
                        onClick={() => toggleField(field.key)}
                        className={cn(
                          'px-3 py-1.5 rounded-lg text-xs font-medium transition-all flex items-center gap-1.5',
                          selectedFields.has(field.key)
                            ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30'
                            : 'bg-[#252525] text-gray-400 hover:text-white border border-transparent hover:border-[#444]',
                        )}
                      >
                        {selectedFields.has(field.key) && (
                          <Check className="w-3 h-3" />
                        )}
                        {field.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Time Fields */}
                <div>
                  <div className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
                    Time Fields
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {EXPORT_FIELDS.filter((f) => f.category === 'time').map(
                      (field) => (
                        <button
                          key={field.key}
                          onClick={() => toggleField(field.key)}
                          className={cn(
                            'px-3 py-1.5 rounded-lg text-xs font-medium transition-all flex items-center gap-1.5',
                            selectedFields.has(field.key)
                              ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30'
                              : 'bg-[#252525] text-gray-400 hover:text-white border border-transparent hover:border-[#444]',
                          )}
                        >
                          {selectedFields.has(field.key) && (
                            <Check className="w-3 h-3" />
                          )}
                          {field.label}
                        </button>
                      ),
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Mode Selection Cards */}
        <div className="grid grid-cols-2 gap-3">
          {/* All Users Card */}
          <button
            onClick={() => {
              setDownloadMode('all')
              clearDateRange()
            }}
            className={cn(
              'relative p-4 rounded-xl border-2 text-left transition-all duration-200',
              downloadMode === 'all'
                ? 'bg-amber-500/10 border-amber-500/50'
                : 'bg-[#1a1a1a] border-[#2a2a2a] hover:border-[#3a3a3a] hover:bg-[#1f1f1f]',
            )}
          >
            {downloadMode === 'all' && (
              <div className="absolute top-3 right-3">
                <div className="w-5 h-5 rounded-full bg-amber-500 flex items-center justify-center">
                  <Check className="w-3 h-3 text-black" />
                </div>
              </div>
            )}
            <div
              className={cn(
                'w-10 h-10 rounded-lg flex items-center justify-center mb-3',
                downloadMode === 'all' ? 'bg-amber-500/20' : 'bg-[#252525]',
              )}
            >
              <Users
                className={cn(
                  'w-5 h-5',
                  downloadMode === 'all' ? 'text-amber-500' : 'text-gray-400',
                )}
              />
            </div>
            <h3
              className={cn(
                'font-semibold mb-1',
                downloadMode === 'all' ? 'text-amber-400' : 'text-white',
              )}
            >
              All Users
            </h3>
            <p className="text-xs text-gray-500">
              Export complete user database
            </p>
          </button>

          {/* Date Range Card */}
          <button
            onClick={() => setDownloadMode('dateRange')}
            className={cn(
              'relative p-4 rounded-xl border-2 text-left transition-all duration-200',
              downloadMode === 'dateRange'
                ? 'bg-amber-500/10 border-amber-500/50'
                : 'bg-[#1a1a1a] border-[#2a2a2a] hover:border-[#3a3a3a] hover:bg-[#1f1f1f]',
            )}
          >
            {downloadMode === 'dateRange' && (
              <div className="absolute top-3 right-3">
                <div className="w-5 h-5 rounded-full bg-amber-500 flex items-center justify-center">
                  <Check className="w-3 h-3 text-black" />
                </div>
              </div>
            )}
            <div
              className={cn(
                'w-10 h-10 rounded-lg flex items-center justify-center mb-3',
                downloadMode === 'dateRange'
                  ? 'bg-amber-500/20'
                  : 'bg-[#252525]',
              )}
            >
              <Calendar
                className={cn(
                  'w-5 h-5',
                  downloadMode === 'dateRange'
                    ? 'text-amber-500'
                    : 'text-gray-400',
                )}
              />
            </div>
            <h3
              className={cn(
                'font-semibold mb-1',
                downloadMode === 'dateRange' ? 'text-amber-400' : 'text-white',
              )}
            >
              By Date Range
            </h3>
            <p className="text-xs text-gray-500">Filter by registration date</p>
          </button>
        </div>

        {/* Date Range Picker */}
        {downloadMode === 'dateRange' && (
          <div className="space-y-4 animate-in fade-in slide-in-from-top-2 duration-200">
            {/* Quick Presets */}
            <div>
              <label className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2 block">
                Quick Select
              </label>
              <div className="flex flex-wrap gap-2">
                {DATE_PRESETS.map((preset) => (
                  <button
                    key={preset.label}
                    onClick={() => selectPreset(preset)}
                    className={cn(
                      'px-3 py-1.5 rounded-lg text-xs font-medium transition-all',
                      selectedPreset === preset.label
                        ? 'bg-amber-500 text-black'
                        : 'bg-[#252525] text-gray-400 hover:bg-[#2a2a2a] hover:text-white border border-[#333]',
                    )}
                  >
                    {preset.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Calendar */}
            <div className="bg-[#1a1a1a] rounded-xl border border-[#2a2a2a] p-4">
              <div className="flex justify-center">
                <DayPicker
                  mode="range"
                  selected={{
                    from: dateRange.from,
                    to: dateRange.to,
                  }}
                  onSelect={(range) => {
                    setDateRange({
                      from: range?.from,
                      to: range?.to,
                    })
                    setSelectedPreset(null)
                  }}
                  numberOfMonths={2}
                  className="rounded-md"
                  classNames={{
                    months:
                      'flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0',
                    month: 'space-y-4',
                    caption:
                      'flex justify-center pt-1 relative items-center text-gray-300',
                    caption_label: 'text-sm font-medium',
                    nav: 'space-x-1 flex items-center',
                    nav_button:
                      'h-7 w-7 bg-[#252525] p-0 hover:bg-[#333] border border-[#333] rounded-md text-gray-400 hover:text-white transition-colors inline-flex items-center justify-center',
                    nav_button_previous: 'absolute left-1',
                    nav_button_next: 'absolute right-1',
                    table: 'w-full border-collapse space-y-1',
                    head_row: 'flex',
                    head_cell:
                      'text-gray-500 rounded-md w-9 font-normal text-[0.8rem]',
                    row: 'flex w-full mt-2',
                    cell: 'h-9 w-9 text-center text-sm p-0 relative [&:has([aria-selected].day-range-end)]:rounded-r-md [&:has([aria-selected].day-outside)]:bg-amber-500/10 [&:has([aria-selected])]:bg-amber-500/20 first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md focus-within:relative focus-within:z-20',
                    day: 'h-9 w-9 p-0 font-normal aria-selected:opacity-100 hover:bg-[#333] rounded-md text-gray-300 transition-colors',
                    day_range_end: 'day-range-end',
                    day_selected:
                      'bg-amber-500 text-black hover:bg-amber-400 hover:text-black focus:bg-amber-500 focus:text-black font-semibold',
                    day_today:
                      'bg-[#333] text-white font-semibold ring-1 ring-amber-500/50',
                    day_outside:
                      'day-outside text-gray-600 opacity-50 aria-selected:bg-amber-500/10 aria-selected:text-gray-400 aria-selected:opacity-30',
                    day_disabled: 'text-gray-600 opacity-50',
                    day_range_middle:
                      'aria-selected:bg-amber-500/20 aria-selected:text-gray-200',
                    day_hidden: 'invisible',
                  }}
                />
              </div>
            </div>

            {/* Selected Range Display */}
            {dateRange.from && (
              <div className="flex items-center justify-between bg-[#1a1a1a] rounded-lg px-4 py-3 border border-[#2a2a2a]">
                <div className="flex items-center gap-3">
                  <Clock className="w-4 h-4 text-amber-500" />
                  <span className="text-sm text-gray-400">Selected range:</span>
                  <span className="text-sm font-medium text-white">
                    {format(dateRange.from, 'MMM dd, yyyy')}
                    {dateRange.to && dateRange.to !== dateRange.from && (
                      <span className="text-gray-500"> → </span>
                    )}
                    {dateRange.to &&
                      dateRange.to !== dateRange.from &&
                      format(dateRange.to, 'MMM dd, yyyy')}
                  </span>
                </div>
                <button
                  onClick={clearDateRange}
                  className="p-1.5 rounded-md hover:bg-[#333] text-gray-500 hover:text-white transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex gap-3 pt-2">
          <Button
            variant="outline"
            className="flex-1 h-11 bg-transparent border-[#333] text-gray-400 hover:bg-[#252525] hover:text-white hover:border-[#444]"
            onClick={() => popModal()}
          >
            Cancel
          </Button>
          <Button
            className="flex-1 h-11 bg-amber-500 text-black hover:bg-amber-400 font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={handleDownload}
            disabled={
              isDownloading ||
              selectedFields.size === 0 ||
              (downloadMode === 'dateRange' && !dateRange.from)
            }
          >
            {isDownloading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Exporting...
              </>
            ) : (
              <>
                <Download className="h-4 w-4 mr-2" />
                Export {selectedFields.size} Fields
              </>
            )}
          </Button>
        </div>
      </div>
    </DialogContent>
  )
}
